import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { UpgradeItemType, UpgradeLabourType, UpgradeLineItem } from '@app/models/closedown-detail.model';
import { JobWithPurchaseOrdersDto, PurchaseOrderDto } from '@app/models/job-with-purchase-orders.model';
import { InvoiceService } from '@app/services/invoice/invoice.service';
import { NavigationService } from '@app/services/navigation/navigation.service';
import { forkJoin } from 'rxjs';
import {
  UpgradeLineItemDialogComponent,
  UpgradeLineItemDialogData
} from '../job-upgrade/upgrade-line-item-dialog.component';

type AttachmentCategory = 'invoice' | 'supporting' | 'serviceSheet';

interface AttachmentRule {
  accept: string;
  maxBytes: number;
}

interface InvoiceLineItem {
  id: string;
  itemID: string;
  itemType: string;
  itemCode: string;
  itemUnitOfMeasure?: string;
  description: string;
  unitPriceExcludingTax: number;
  unitTax: number;
  quantity: number;
  isAdded: boolean;
}

@Component({
  selector: 'app-invoice',
  templateUrl: './invoice.component.html',
  styleUrls: ['./invoice.component.scss'],
  standalone: false
})
export class InvoiceComponent implements OnInit {
  readonly form = this.formBuilder.group({
    invoiceNumber: ['', [Validators.required, Validators.maxLength(100)]],
    invoiceDate: [this.startOfToday(), Validators.required]
  });
  readonly maxInvoiceDate = this.startOfToday();
  readonly attachmentRules: Record<AttachmentCategory, AttachmentRule> = {
    invoice: { accept: 'application/pdf,.pdf', maxBytes: 4 * 1024 * 1024 },
    supporting: { accept: 'application/pdf,image/jpeg,.pdf,.jpg,.jpeg', maxBytes: 10 * 1024 * 1024 },
    serviceSheet: { accept: 'application/pdf,image/jpeg,.pdf,.jpg,.jpeg', maxBytes: 10 * 1024 * 1024 }
  };
  readonly attachments: Record<AttachmentCategory, File[]> = {
    invoice: [],
    supporting: [],
    serviceSheet: []
  };

  job?: JobWithPurchaseOrdersDto;
  hasLoaded = false;
  hasLoadError = false;
  submissionUnavailable = false;
  attachmentError = '';
  readonly lineItemsByPurchaseOrder: Record<string, InvoiceLineItem[]> = {};
  itemTypes: UpgradeItemType[] = [];
  labourTypes: UpgradeLabourType[] = [];
  readonly taxRate = 0.1;
  private lineSequence = 0;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly dialog: MatDialog,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly invoiceService: InvoiceService,
    private readonly navigationService: NavigationService
  ) {
    this.navigationService.onChange(true, false);
  }

  get jobNumber(): string {
    if (!this.job) {
      return '-';
    }

    return this.job.referenceNumber || this.job.calloutReferenceNumber || this.job.purchaseOrders[0]?.jobNumber || '-';
  }

  get purchaseOrderNumbers(): string {
    return this.job?.purchaseOrders
      .map(purchaseOrder => purchaseOrder.purchaseOrderNumber || purchaseOrder.purchaseOrderReferenceId)
      .filter((value): value is string => Boolean(value))
      .join(', ') || '-';
  }

  get currencies(): string[] {
    return [...new Set(this.job?.purchaseOrders.map(purchaseOrder => this.currencyCode(purchaseOrder.currencyCode)) ?? [])];
  }

  currencyCode(currency?: string): string {
    const normalized = currency?.trim().toUpperCase();
    if (normalized && /^[A-Z]{3}$/.test(normalized)) {
      return normalized;
    }

    return ({ '$': 'AUD', 'A$': 'AUD', '£': 'GBP', '€': 'EUR' } as Record<string, string>)[currency?.trim() ?? ''] ?? 'AUD';
  }

  ngOnInit(): void {
    const jobId = this.route.snapshot.paramMap.get('jobId');
    if (!jobId) {
      this.hasLoadError = true;
      this.hasLoaded = true;
      return;
    }

    forkJoin({
      job: this.invoiceService.getJobWithApprovedReceipt(jobId),
      itemTypes: this.invoiceService.getUpgradeItemTypes(),
      labourTypes: this.invoiceService.getUpgradeLabourTypes()
    }).subscribe({
      next: result => {
        this.job = result.job;
        this.itemTypes = result.itemTypes;
        this.labourTypes = result.labourTypes;
        this.initializeLineItems(result.job);
        this.hasLoaded = true;
      },
      error: () => {
        this.hasLoadError = true;
        this.hasLoaded = true;
      }
    });
  }

  goBack(): void {
    void this.router.navigate(['jobsawaitinginvoice']);
  }

  selectFiles(event: Event, category: AttachmentCategory): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    const rule = this.attachmentRules[category];
    this.attachmentError = '';

    for (const file of files) {
      if (!this.isAllowedFile(file, category)) {
        this.attachmentError = file.size > rule.maxBytes
          ? 'invoice_attachment_size_error'
          : 'invoice_attachment_type_error';
        continue;
      }

      if (this.attachments[category].some(existing => existing.name.toLowerCase() === file.name.toLowerCase())) {
        this.attachmentError = 'invoice_attachment_duplicate_error';
        continue;
      }

      this.attachments[category].push(file);
    }

    input.value = '';
  }

  removeFile(category: AttachmentCategory, index: number): void {
    this.attachments[category].splice(index, 1);
  }

  attachmentFiles(category: string): File[] {
    return this.attachments[category as AttachmentCategory] ?? [];
  }

  lineItems(purchaseOrder: PurchaseOrderDto): InvoiceLineItem[] {
    return this.lineItemsByPurchaseOrder[purchaseOrder.id] ?? [];
  }

  openAddLineItem(purchaseOrder: PurchaseOrderDto): void {
    this.openLineItemDialog(purchaseOrder);
  }

  openEditLineItem(purchaseOrder: PurchaseOrderDto, line: InvoiceLineItem): void {
    this.openLineItemDialog(purchaseOrder, line);
  }

  removeLineItem(purchaseOrder: PurchaseOrderDto, line: InvoiceLineItem): void {
    if (!line.isAdded) {
      return;
    }

    const lines = this.lineItems(purchaseOrder);
    const index = lines.indexOf(line);
    if (index >= 0) {
      lines.splice(index, 1);
    }
  }

  lineNet(line: InvoiceLineItem): number {
    return line.quantity * line.unitPriceExcludingTax;
  }

  lineTax(line: InvoiceLineItem): number {
    return line.quantity * line.unitTax;
  }

  lineGross(line: InvoiceLineItem): number {
    return this.lineNet(line) + this.lineTax(line);
  }

  purchaseOrderNet(purchaseOrder: PurchaseOrderDto): number {
    return this.lineItems(purchaseOrder).reduce((total, line) => total + this.lineNet(line), 0);
  }

  purchaseOrderTax(purchaseOrder: PurchaseOrderDto): number {
    return this.lineItems(purchaseOrder).reduce((total, line) => total + this.lineTax(line), 0);
  }

  purchaseOrderGross(purchaseOrder: PurchaseOrderDto): number {
    return this.purchaseOrderNet(purchaseOrder) + this.purchaseOrderTax(purchaseOrder);
  }

  totalNet(currency: string): number {
    return this.job?.purchaseOrders
      .filter(purchaseOrder => this.currencyCode(purchaseOrder.currencyCode) === currency)
      .reduce((total, purchaseOrder) => total + this.purchaseOrderNet(purchaseOrder), 0) ?? 0;
  }

  totalTax(currency: string): number {
    return this.purchaseOrdersForCurrency(currency)
      .reduce((total, purchaseOrder) => total + this.purchaseOrderTax(purchaseOrder), 0);
  }

  totalGross(currency: string): number {
    return this.totalNet(currency) + this.totalTax(currency);
  }

  averagePurchaseOrderCost(currency: string): number {
    const purchaseOrders = this.purchaseOrdersForCurrency(currency);
    if (purchaseOrders.length === 0) {
      return 0;
    }

    return purchaseOrders.reduce((total, purchaseOrder) => total + purchaseOrder.amountExcludingTax, 0)
      / purchaseOrders.length;
  }

  submit(): void {
    this.form.markAllAsTouched();
    this.submissionUnavailable = this.form.valid && this.attachments.invoice.length > 0;
  }

  private isAllowedFile(file: File, category: AttachmentCategory): boolean {
    const rule = this.attachmentRules[category];
    const extension = file.name.split('.').pop()?.toLowerCase();
    const allowedExtensions = category === 'invoice' ? ['pdf'] : ['pdf', 'jpg', 'jpeg'];
    return file.size <= rule.maxBytes && Boolean(extension && allowedExtensions.includes(extension));
  }

  private initializeLineItems(job: JobWithPurchaseOrdersDto): void {
    for (const purchaseOrder of job.purchaseOrders) {
      this.lineItemsByPurchaseOrder[purchaseOrder.id] = purchaseOrder.purchaseOrderLines.map(line => {
        const itemType = this.itemTypes.find(option => option.itemCode === line.itemCode);
        return {
          id: line.id,
          itemID: itemType?.id || line.itemReferenceId || '',
          itemType: itemType?.name || line.itemCode || '',
          itemCode: line.itemCode || '',
          ...(line.itemUnitOfMeasure ? { itemUnitOfMeasure: line.itemUnitOfMeasure } : {}),
          description: line.description || '',
          unitPriceExcludingTax: line.unitPriceExcludingTax,
          unitTax: this.round(line.unitPriceExcludingTax * this.taxRate),
          quantity: line.quantity,
          isAdded: false
        };
      });
    }
  }

  private openLineItemDialog(purchaseOrder: PurchaseOrderDto, line?: InvoiceLineItem): void {
    const currency = this.currencyCode(purchaseOrder.currencyCode);
    const data: UpgradeLineItemDialogData = {
      itemTypes: this.itemTypes,
      labourTypes: this.labourTypes,
      taxRate: this.taxRate,
      currency: this.currencySymbol(currency),
      context: 'invoice',
      ...(line ? { lineItem: this.toUpgradeLineItem(line) } : {})
    };
    const dialogRef = this.dialog.open(UpgradeLineItemDialogComponent, {
      width: '38rem',
      maxWidth: 'calc(100vw - 2rem)',
      maxHeight: 'calc(100dvh - 2rem)',
      disableClose: true,
      data
    });

    dialogRef.afterClosed().subscribe((result?: UpgradeLineItem) => {
      if (!result) {
        return;
      }

      const mappedLine: InvoiceLineItem = {
        id: line?.id || `added-line-${++this.lineSequence}`,
        itemID: result.itemID,
        itemType: result.itemType,
        itemCode: result.itemCode,
        ...(result.itemUnitOfMeasure ? { itemUnitOfMeasure: result.itemUnitOfMeasure } : {}),
        description: result.itemDescription,
        unitPriceExcludingTax: result.unitPrice,
        unitTax: result.itemQuantity > 0 ? this.round(result.lineTax / result.itemQuantity) : 0,
        quantity: result.itemQuantity,
        isAdded: line?.isAdded ?? true
      };
      const lines = this.lineItems(purchaseOrder);
      const index = line ? lines.indexOf(line) : -1;
      this.lineItemsByPurchaseOrder[purchaseOrder.id] = index >= 0
        ? lines.map((item, itemIndex) => itemIndex === index ? mappedLine : item)
        : [...lines, mappedLine];
    });
  }

  private toUpgradeLineItem(line: InvoiceLineItem): UpgradeLineItem {
    return {
      itemID: line.itemID,
      itemType: line.itemType,
      itemCode: line.itemCode,
      ...(line.itemUnitOfMeasure ? { itemUnitOfMeasure: line.itemUnitOfMeasure } : {}),
      itemDescription: line.description,
      itemQuantity: line.quantity,
      unitPrice: line.unitPriceExcludingTax,
      lineTax: this.lineTax(line),
      lineGross: this.lineGross(line)
    };
  }

  private currencySymbol(currency: string): string {
    return new Intl.NumberFormat('en', { style: 'currency', currency })
      .formatToParts(0)
      .find(part => part.type === 'currency')?.value || currency;
  }

  private round(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  private purchaseOrdersForCurrency(currency: string): PurchaseOrderDto[] {
    return this.job?.purchaseOrders.filter(
      purchaseOrder => this.currencyCode(purchaseOrder.currencyCode) === currency
    ) ?? [];
  }

  private startOfToday(): Date {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }
}